<template>
  <div id="app">
    <AdminNavigation v-if="showNav" :collapsed="navCollapsed" @toggle="toggleNav" />
    <main :class="['main-content', { 'with-nav': showNav, 'nav-collapsed': showNav && navCollapsed }]">
      <RouterView />
    </main>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute, RouterView } from 'vue-router'
import AdminNavigation from '@/components/AdminNavigation.vue'

const route = useRoute()
const showNav = computed(() => route.path !== '/login')

const NAV_COLLAPSED_KEY = 'admin_nav_collapsed'
const navCollapsed = ref(localStorage.getItem(NAV_COLLAPSED_KEY) === '1')
function toggleNav() {
  navCollapsed.value = !navCollapsed.value
  localStorage.setItem(NAV_COLLAPSED_KEY, navCollapsed.value ? '1' : '0')
}
</script>

<style>
* { box-sizing: border-box; margin: 0; padding: 0; }

body {
  background: var(--bg-base);
  color: var(--text-primary);
  font-family: Inter, system-ui, sans-serif;
}

#app { min-height: 100vh; }

.main-content { min-height: 100vh; transition: margin-left 0.18s ease; }
.main-content.with-nav { margin-left: var(--nav-width); }
.main-content.with-nav.nav-collapsed { margin-left: var(--nav-width-collapsed); }

/* ── Shared admin-view layout ── */
.admin-view { padding: 2rem; }

.view-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.75rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid var(--border-subtle);
}
.view-header h2 { font-size: 1.5rem; }

.toolbar { display: flex; gap: 0.75rem; align-items: center; }

/* ── Buttons ── */
.btn {
  padding: 0.55rem 1.1rem;
  border-radius: var(--radius-sm);
  font-size: 0.875rem;
  cursor: pointer;
  border: none;
  transition: opacity 0.15s;
}
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-primary  { background: linear-gradient(135deg, var(--accent), #764ba2); color: var(--text-primary); }
.btn-outline  { background: transparent; border: 1px solid var(--border-default); color: var(--text-primary); }
.btn-sm       { padding: 0.35rem 0.75rem; font-size: 0.8rem; }

/* ── Generic list + card ── */
.list { display: grid; gap: 0.75rem; }

.card {
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  padding: 1.1rem 1.4rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}
.card strong { display: block; margin-bottom: 0.2rem; }
.card p      { color: var(--text-secondary); margin: 0 0 0.2rem; font-size: 0.875rem; }
.card small  { color: var(--text-muted); font-size: 0.78rem; }
.card-actions { display: flex; gap: 0.5rem; flex-shrink: 0; }
.card-meta { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }

/* ── Feedback ── */
.empty      { color: var(--text-muted); padding: 3rem 0; text-align: center; }
.empty-inline { color: var(--text-muted); font-size: 0.875rem; }
.load-error { color: var(--danger-text); margin-bottom: 1rem; }
.loading-state {
  color: var(--text-muted);
  padding: 3rem 0;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
}
.loading-state::before {
  content: '';
  width: 1rem;
  height: 1rem;
  border: 2px solid var(--text-muted);
  border-top-color: transparent;
  border-radius: 50%;
  animation: ppspin 0.7s linear infinite;
}
@keyframes ppspin { to { transform: rotate(360deg); } }
.error      { color: var(--danger-text); font-size: 0.875rem; margin-bottom: 0.75rem; }
.muted      { color: var(--text-secondary); font-size: 0.9rem; }

/* ── Modal ── */
.modal {
  position: fixed;
  inset: 0;
  background: var(--bg-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
}
.modal-content {
  background: var(--bg-elevated);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  padding: 2rem;
  width: 90%;
  max-width: 460px;
  max-height: 90vh;
  overflow-y: auto;
}
.modal-content h3 { margin: 0 0 1.25rem; font-size: 1.15rem; }
.modal-actions    { display: flex; gap: 0.75rem; justify-content: flex-end; margin-top: 1.5rem; }

/* ── Forms ── */
.form-group       { margin-bottom: 1rem; }
.form-group label { display: block; margin-bottom: 0.35rem; font-size: 0.875rem; color: var(--text-secondary); }
.optional         { color: var(--text-muted); font-size: 0.78rem; }
.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 0.65rem;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-sm);
  background: var(--bg-input);
  color: var(--text-primary);
  font-size: 0.9rem;
}
.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus { outline: none; border-color: var(--border-focus); }
.form-group textarea { resize: vertical; }

/* Keep browser autofill on the dark theme — Chrome/Edge otherwise paint a
   light-mode background on autofilled email/password fields. There is no way
   to set the background directly, so we use an inset shadow to cover it and
   pin the text colour via -webkit-text-fill-color. */
.form-group input:-webkit-autofill,
.form-group input:-webkit-autofill:hover,
.form-group input:-webkit-autofill:focus,
.form-group input:-webkit-autofill:active {
  -webkit-text-fill-color: var(--text-primary);
  -webkit-box-shadow: 0 0 0 1000px var(--bg-elevated) inset;
  box-shadow: 0 0 0 1000px var(--bg-elevated) inset;
  caret-color: var(--text-primary);
  transition: background-color 9999s ease-in-out 0s;
}

/* ── Filter select ── */
.filter-select {
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-sm);
  background: var(--bg-input);
  color: var(--text-primary);
  font-size: 0.875rem;
  cursor: pointer;
}

/* Native <option> popup theming (fallback alongside color-scheme:dark).
   Applies to every select in the app — the opened dropdown list. */
select option,
select optgroup {
  background: var(--bg-elevated);
  color: var(--text-primary);
}

/* ── Status badges (global — used by multiple views) ── */
.status-badge {
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.status-new       { background: var(--status-new-bg);       color: var(--status-new-text); }
.status-contacted { background: var(--status-contacted-bg); color: var(--status-contacted-text); }
.status-converted { background: var(--status-converted-bg); color: var(--status-converted-text); }
.status-closed    { background: var(--status-closed-bg);    color: var(--status-closed-text); }

/* ── Tier badges (global — used by GalleriesView and ClientDashboard) ── */
.tier-badge {
  font-size: 0.68rem;
  font-weight: 600;
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.tier-proof    { background: var(--tier-proof-bg);    color: var(--tier-proof-text); }
.tier-preview  { background: var(--tier-preview-bg);  color: var(--tier-preview-text); }
.tier-delivery { background: var(--tier-delivery-bg); color: var(--tier-delivery-text); }

/* ── Upload modal ── */
.upload-modal { max-width: 560px; }
.drop-zone {
  border: 2px dashed var(--border-default);
  border-radius: var(--radius-md);
  padding: 1.75rem;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  margin-bottom: 1.1rem;
  color: var(--text-secondary);
  font-size: 0.9rem;
}
.drop-zone:hover, .drop-zone.drag-over { border-color: var(--accent); background: var(--accent-dim); }
.drop-zone .link { color: var(--accent); text-decoration: underline; }
.upload-list {
  max-height: 190px;
  overflow-y: auto;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-sm);
  margin-bottom: 1rem;
}
.upload-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.45rem 0.7rem;
  border-bottom: 1px solid var(--border-subtle);
  font-size: 0.82rem;
}
.upload-item:last-child { border-bottom: none; }
.upload-name  { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--text-secondary); }
.upload-status { margin-left: 1rem; font-size: 0.8rem; }
.upload-status.pending   { color: var(--text-muted); }
.upload-status.uploading { color: var(--status-contacted-text); }
.upload-status.done      { color: var(--status-converted-text); }
.upload-status.error     { color: var(--danger-text); }

/* ── Reorder modal ── */
.reorder-modal { max-width: 700px; }
.reorder-hint  { color: var(--text-secondary); font-size: 0.82rem; margin: -0.5rem 0 1.1rem; }
.reorder-loading { color: var(--text-secondary); padding: 2rem; text-align: center; }
.reorder-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 0.5rem;
  max-height: 440px;
  overflow-y: auto;
  margin-bottom: 1.1rem;
  padding: 0.2rem;
}
.reorder-item {
  position: relative;
  border-radius: var(--radius-sm);
  overflow: hidden;
  border: 2px solid transparent;
  cursor: grab;
  user-select: none;
  transition: border-color 0.15s, opacity 0.15s;
}
.reorder-item:active { cursor: grabbing; }
.reorder-item.dragging    { opacity: 0.35; }
.reorder-item.drag-target { border-color: var(--accent); }
.reorder-item img {
  width: 100%;
  height: 80px;
  object-fit: cover;
  display: block;
  pointer-events: none;
}
.reorder-num {
  position: absolute;
  top: 3px;
  left: 5px;
  background: var(--bg-overlay);
  color: var(--text-primary);
  font-size: 0.65rem;
  font-weight: 700;
  padding: 1px 4px;
  border-radius: 4px;
  pointer-events: none;
}

/* ── Download section (GalleriesView Edit modal) ── */
.download-section { margin-top: 1rem; }
.download-status  { margin-bottom: 0.6rem; font-size: 0.83rem; display: flex; flex-wrap: wrap; gap: 0.4rem; align-items: center; }
.dl-active   { color: var(--status-converted-text); }
.dl-expired  { color: var(--status-contacted-text); }
.dl-inactive { color: var(--text-muted); }
.dl-built    { color: var(--text-muted); }
.download-controls { display: flex; flex-direction: column; gap: 0.6rem; }
.download-btns { display: flex; gap: 0.5rem; flex-wrap: wrap; }
.checkbox-label { display: flex; align-items: center; gap: 0.4rem; font-size: 0.875rem; color: var(--text-secondary); cursor: pointer; }

/* ── Settings ── */
.settings-section { margin-bottom: 2rem; }
.settings-section h3 { font-size: 1rem; margin-bottom: 0.5rem; color: var(--text-secondary); }

@media (max-width: 768px) {
  .main-content.with-nav,
  .main-content.with-nav.nav-collapsed { margin-left: 0; }
  .view-header { flex-direction: column; align-items: flex-start; gap: 0.75rem; }
}
</style>
